import { getQuote } from "../api/ProcurementClient";
import Rfq from "../models/Rfq";
import { RfqItem } from "../models/RfqItem";
import {
  getRfqClientEpoch,
  mergeFetchedRfqIntoList,
} from "./mergeRfqIntoList";

/** Find the RFQ that currently contains `item`. */
export function findOwningRfqId(
  requestForQuotes: Rfq[],
  itemId: number
): number | undefined {
  for (const rfq of requestForQuotes) {
    for (const hw of rfq.quotes ?? []) {
      if ((hw.items ?? []).some((i) => i.id === itemId)) {
        return rfq.id;
      }
    }
  }
  return undefined;
}

/** Find RFQ ids that contain line items linked to `item` via import/export. */
export function findLinkedRfqIds(
  requestForQuotes: Rfq[],
  item: Pick<RfqItem, "id" | "sourceRfqId" | "sourceRfqItemId">
): number[] {
  const rootId = item.sourceRfqItemId ?? item.id;
  const linked = new Set<number>();

  for (const rfq of requestForQuotes) {
    for (const hw of rfq.quotes ?? []) {
      for (const other of hw.items ?? []) {
        const sameRoot =
          other.id === rootId ||
          other.sourceRfqItemId === rootId ||
          (item.sourceRfqItemId != null &&
            other.sourceRfqItemId === item.sourceRfqItemId);
        if (sameRoot && other.id !== item.id) {
          linked.add(rfq.id);
        }
      }
    }
    if (item.sourceRfqId != null && item.sourceRfqId === rfq.id) {
      linked.add(rfq.id);
    }
  }

  // Exclude RFQs that currently contain the edited item itself.
  for (const rfq of requestForQuotes) {
    const containsEdited = (rfq.quotes ?? []).some((hw) =>
      (hw.items ?? []).some((i) => i.id === item.id)
    );
    if (containsEdited) {
      linked.delete(rfq.id);
    }
  }

  return Array.from(linked);
}

/**
 * Refetch the owning RFQ (and any linked RFQs) after a line/quote change so
 * assignmentHistory and server-synced fields stay fresh without a full page reload.
 */
export async function refreshLinkedRfqsForItem(
  item: Pick<RfqItem, "id" | "sourceRfqId" | "sourceRfqItemId">,
  requestForQuotes: Rfq[],
  setRfqs: (updater: (prev: Rfq[]) => Rfq[]) => void
): Promise<void> {
  const rfqIds = new Set(findLinkedRfqIds(requestForQuotes, item));
  const owningId = findOwningRfqId(requestForQuotes, item.id);
  if (owningId != null) {
    rfqIds.add(owningId);
  }
  if (rfqIds.size === 0) {
    return;
  }

  const ids = Array.from(rfqIds);
  const epochsAtStart = new Map(
    ids.map((id) => [id, getRfqClientEpoch(id)] as const)
  );
  const refreshed = await Promise.all(
    ids.map((id) => getQuote(id).catch(() => null))
  );
  setRfqs((prev) => {
    let next = prev;
    for (let i = 0; i < refreshed.length; i++) {
      const rfq = refreshed[i];
      if (rfq) {
        next = mergeFetchedRfqIntoList(
          next,
          rfq,
          epochsAtStart.get(ids[i]) ?? 0
        );
      }
    }
    return next;
  });
}

/** Refetch a single RFQ into AppContext (e.g. after opening History). */
export async function refreshRfqInList(
  rfqId: number,
  setRfqs: (updater: (prev: Rfq[]) => Rfq[]) => void,
  options?: { includeHistory?: boolean }
): Promise<Rfq | null> {
  const epochAtStart = getRfqClientEpoch(rfqId);
  const rfq = await getQuote(rfqId, options).catch(() => null);
  if (rfq) {
    setRfqs((prev) => mergeFetchedRfqIntoList(prev, rfq, epochAtStart));
  }
  return rfq;
}
